import { createContext, useContext, type ReactNode } from "react";
import styled from "styled-components";
import { Flex } from "src/elements";
import { OMark } from "./OSummary";

export const OTouchedContext = createContext<Readonly<Record<string, string>> | null>(null);

export const useOTouched = (path?: string | readonly string[]) => {
  const marks = useContext(OTouchedContext);
  if (!marks || path === undefined) return null;
  const lines = (typeof path === "string" ? [path] : path).flatMap((key) =>
    marks[key] ? [marks[key]] : [],
  );
  return lines.length > 0 ? lines.join("\n") : null;
};

// Read OMark during render to avoid the import cycle through src/elements.

const MARK_SIZE = { width: 14, height: 14, borderWidth: 3, cursor: "help" } as const;

const Mark = ({ line }: { line: string }) => (
  <OMark role="img" aria-label={line} title={line} data-testid="o-touched" style={MARK_SIZE} />
);

export const OTouchedMark = ({ path }: { path?: string | readonly string[] }) => {
  const line = useOTouched(path);
  return line ? <Mark line={line} /> : null;
};

export const OTouchedTitle = ({
  path,
  children,
}: {
  path?: string | readonly string[];
  children: ReactNode;
}) => {
  const line = useOTouched(path);
  if (!line) return children;
  return (
    <Flex justify="center" align="center" gap="6px">
      {children}
      <Mark line={line} />
    </Flex>
  );
};

const Row = styled.div`
  position: relative;
`;

const Corner = styled.span`
  position: absolute;
  top: 2px;
  right: 0;
`;

export const OTouchedRow = ({
  path,
  children,
}: {
  path: string | readonly string[];
  children: ReactNode;
}) => {
  const line = useOTouched(path);
  if (!line) return children;
  return (
    <Row>
      {children}
      <Corner>
        <Mark line={line} />
      </Corner>
    </Row>
  );
};
